import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

export default function ListAttendanceStatistic() {
  const { teach_id } = useParams(); // get the room_id, class, room number from the URL

  const { setLoading } = useLoading();
  ///// Skin Switch /////
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');

      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }

    // Toggle dark mode for all tables
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead'); // หาทุก thead ภายใน table
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  }

  switchSkin(skin);

  useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  const [data, setData] = useState({ attendanceData: [] });
  const [yearDisplay, setYearDisplay] = useState("");

  useEffect(() => {
    const yearId = localStorage.getItem('yearId');
    if (!yearId) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const found = res.data.find(y => String(y.year_id) === String(yearId));
      if (found) setYearDisplay(`ปีการศึกษา ${found.year_year}`);
    })
    .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
        
    axiosInstance.get(`${process.env.REACT_APP_API}/getAttendanceStatistic/${teach_id}`, 
    {
      headers: {
          authorization: `Bearer ${getToken()}`
      }
    })
      .then(response => {
        setData(response.data)
        // setIsLoading(false)
        // console.log(response.data)
      })
      .catch(err => {
        console.log(err.response.data.message)
        Swal.fire({
          title: err.response.data.title || err.response.statusText,
          text: err.response.data.message || err.message,
          icon: "error"
        })
      }).finally(() => {
        setLoading(false);
      })

  }, [setLoading, teach_id]);

  const attendanceData = data?.attendanceData?.[0] || null
  const students = attendanceData?.students || []
  const attendance = attendanceData?.attendance || []

  // Each column is a distinct class session (date + period), ordered chronologically.
  const sessions = (() => {
    const seen = new Set()
    const list = []
    attendance.forEach(item => {
      const key = `${item.att_date}__${item.period_order}`
      if (!seen.has(key)) {
        seen.add(key)
        list.push({ key, att_date: item.att_date, period_order: item.period_order })
      }
    })
    return list
  })()

  const attendanceMap = new Map()
  attendance.forEach(item => {
    attendanceMap.set(`${item.study_id}__${item.att_date}__${item.period_order}`, item.att_status)
  })

  const getAttendanceClass = (status) => {
    if (status === 'มา') return 'bg-success text-white'
    if (status === 'สาย') return 'bg-warning'
    if (status === 'ลา') return 'bg-info'
    if (status === 'ขาด') return 'bg-danger text-white'
    return ''
  }

  // Flag students who left this room (transferred) or were withdrawn from school.
  const getStudentFlag = (student) => {
    if (student?.is_withdrawn) return { label: 'จำหน่ายแล้ว', rowClass: 'table-danger', badgeClass: 'bg-danger', bg: '#f8d7da' }
    if (student?.is_transferred) return { label: student.transfer_room ? `ย้ายไป ${student.transfer_room}` : 'ย้ายห้อง', rowClass: 'table-warning', badgeClass: 'bg-warning text-dark', bg: '#fff3cd' }
    return null
  }

  const pageTitle = attendanceData
    ? `สถิติการเข้าเรียน ม.${attendanceData.c_id}/${attendanceData.r_no} วิชา ${attendanceData.sub_subname} (${attendanceData.sub_subid})`
    : 'สถิติการเข้าเรียน'

  const escHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const formatDateShort = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-'
    const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${iso[3]}/${iso[2]}`
    const dm = dateStr.match(/^(\d{1,2})\/(\d{1,2})/)
    if (dm) return `${dm[1].padStart(2, '0')}/${dm[2].padStart(2, '0')}`
    return dateStr
  }

  const buildTableRows = (tdStyle) =>
    students.map((student, index) => {
      const flag = getStudentFlag(student)
      const rowBg = flag ? `background:${flag.bg};` : ''
      const cells = sessions.map(session => {
        const status = attendanceMap.get(`${student.study_id}__${session.key}`) || ''
        let bg = ''
        if (status === 'มา') bg = 'background:#198754;color:#fff;'
        else if (status === 'สาย') bg = 'background:#ffc107;'
        else if (status === 'ลา') bg = 'background:#0dcaf0;'
        else if (status === 'ขาด') bg = 'background:#dc3545;color:#fff;'
        return `<td style="${tdStyle}text-align:center;${bg}">${escHtml(status)}</td>`
      }).join('')
      const nameLabel = flag ? ` <span style="color:#b02a37;font-size:0.85em;">(${escHtml(flag.label)})</span>` : ''
      return `<tr>
        <td style="${tdStyle}text-align:center;${rowBg}">${index + 1}</td>
        <td style="${tdStyle}text-align:center;${rowBg}">${escHtml(student.st_idnumber)}</td>
        <td style="${tdStyle}padding-left:6px;${rowBg}">${escHtml(student.st_name)}${nameLabel}</td>
        ${cells}
      </tr>`
    }).join('')

  const buildTheadCells = (thStyle) =>
    sessions.map(session =>
      `<th style="${thStyle}text-align:center;width:28px;">
        <span style="writing-mode:vertical-lr;display:inline-block;line-height:1;">
          ${escHtml(formatDateShort(session.att_date))}
        </span>
      </th>`
    ).join('')

  const handlePrint = () => {
    const thStyle = 'border:1px solid #999;padding:2px 3px;background:#ddeeff;'
    const tdStyle = 'border:1px solid #999;padding:2px 3px;'
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escHtml(pageTitle)}</title>
      <style>
        body { font-family: 'Cordia New', Tahoma, Arial, sans-serif; font-size: 13px; padding: 10px; }
        table { border-collapse: collapse; width: 100%; }
      </style></head>
      <body onload="window.print()">
        <h3 style="text-align:center;margin-bottom:4px;">${escHtml(pageTitle)}</h3>
        ${yearDisplay ? `<div style="text-align:center;margin-bottom:12px;">${escHtml(yearDisplay)}</div>` : ''}
        <table><thead><tr>
          <th style="${thStyle}width:40px;">เลขที่</th>
          <th style="${thStyle}width:100px;">เลขประจำตัว</th>
          <th style="${thStyle}">ชื่อ-สกุล</th>
          ${buildTheadCells(thStyle)}
        </tr></thead>
        <tbody>${buildTableRows(tdStyle) || `<tr><td colspan="${3 + sessions.length}" style="${tdStyle}text-align:center;">ไม่พบข้อมูล</td></tr>`}</tbody>
        </table>
      </body></html>`
    const win = window.open('', '_blank', 'width=1200,height=900')
    win.document.write(html)
    win.document.close()
  }

  const handleExportPdf = async () => {
    const { default: html2canvas } = await import('html2canvas')
    const thStyle = 'border:1px solid #ddd;padding:2px 3px;font-size:10px;background:#ddeeff;'
    const tdStyle = 'border:1px solid #ddd;padding:2px 3px;font-size:10px;'
    const elWidth = Math.max(700, 40 + 100 + 180 + sessions.length * 30 + 24)
    const el = document.createElement('div')
    el.style.cssText = `position:fixed;left:-99999px;top:0;width:${elWidth}px;padding:12px;background:#fff;color:#111;font-family:Tahoma,Arial,sans-serif;`
    el.innerHTML = `
      <h3 style="text-align:center;margin:0 0 4px 0;font-size:13px;">${escHtml(pageTitle)}</h3>
      ${yearDisplay ? `<div style="text-align:center;margin-bottom:8px;font-size:11px;">${escHtml(yearDisplay)}</div>` : ''}
      <table style="border-collapse:collapse;width:100%;"><thead><tr>
        <th style="${thStyle}width:35px;">เลขที่</th>
        <th style="${thStyle}width:90px;">เลขประจำตัว</th>
        <th style="${thStyle}min-width:160px;">ชื่อ-สกุล</th>
        ${buildTheadCells(thStyle)}
      </tr></thead>
      <tbody>${buildTableRows(tdStyle) || `<tr><td colspan="${3 + sessions.length}" style="${tdStyle}text-align:center;">ไม่พบข้อมูล</td></tr>`}</tbody>
      </table>`
    document.body.appendChild(el)
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 })
    document.body.removeChild(el)
    const pageW = 595.28
    const pageH = 841.89
    const margin = 15
    const usableW = pageW - margin * 2
    const imgH = (canvas.height * usableW) / canvas.width
    const imgData = canvas.toDataURL('image/png')
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
    let y = margin
    let remaining = imgH
    while (remaining > 0) {
      doc.addImage(imgData, 'PNG', margin, y, usableW, imgH)
      remaining -= (pageH - margin * 2)
      if (remaining > 0) { doc.addPage(); y = margin - (imgH - remaining) }
    }
    doc.save(`สถิติการเข้าเรียน_${attendanceData?.sub_subid || teach_id}.pdf`)
  }

  const handleExportExcel = () => {
    const rows = [
      [pageTitle],
      [yearDisplay || ''],
      [`ส่งออก: ${new Date().toLocaleString('th-TH')}`],
      [],
      ['เลขที่', 'เลขประจำตัว', 'ชื่อ-สกุล', ...sessions.map(s => formatDateShort(s.att_date))]
    ]
    students.forEach((student, index) => {
      const flag = getStudentFlag(student)
      rows.push([
        index + 1,
        student.st_idnumber,
        flag ? `${student.st_name} (${flag.label})` : student.st_name,
        ...sessions.map(s => attendanceMap.get(`${student.study_id}__${s.key}`) || '')
      ])
    })
    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = [{ wch: 6 }, { wch: 14 }, { wch: 30 }, ...sessions.map(() => ({ wch: 8 }))]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'สถิติการเข้าเรียน')
    XLSX.writeFile(wb, `สถิติการเข้าเรียน_${attendanceData?.sub_subid || teach_id}.xlsx`)
  }

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการสอน</Link></li>
              <li className="breadcrumb-item"><Link to="#">แสดงสถิติการเข้าเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">สถิติการเข้าเรียน ม. {attendanceData ? `${attendanceData.c_id}/${attendanceData.r_no}` : '-'}</li>
            </ol>
            <h4 className="main-title mb-0">แสดงสถิติการเข้าเรียน ม. {attendanceData ? `${attendanceData.c_id}/${attendanceData.r_no}` : '-'} วิชา {attendanceData ? attendanceData.sub_subname : '-'} ({attendanceData ? attendanceData.sub_subid : '-'})</h4>
          </div>
          {yearDisplay && (
            <div className="text-end mt-2 mt-sm-0">
              <span className="badge bg-primary fs-6 fw-normal">{yearDisplay}</span>
            </div>
          )}
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-end gap-1 flex-wrap">
                <Button variant="outline-secondary" size="sm" onClick={handlePrint} disabled={students.length === 0}>
                  <i className="ri-printer-line me-1"></i>พิมพ์
                </Button>
                <Button variant="outline-danger" size="sm" onClick={handleExportPdf} disabled={students.length === 0}>
                  <i className="ri-file-pdf-line me-1"></i>Export PDF
                </Button>
                <Button variant="outline-success" size="sm" onClick={handleExportExcel} disabled={students.length === 0}>
                  <i className="ri-file-excel-line me-1"></i>Export Excel
                </Button>
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive bordered className="mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="text-center" style={{ width:"50px" }}>เลขที่</th>
                      <th scope="col" className="text-center" style={{ width: "100px" }}>เลขประจำตัว</th>
                      <th scope="col">ชื่อ-สกุล</th>
                      {sessions.map(session => (
                        <th key={session.key} scope="col" className="text-center" style={{ width: "30px", padding: "4px 2px" }}>
                          <span className="d-inline-block" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}>
                            {formatDateShort(session.att_date)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                  {students.length > 0 ? (
                    students.map((student, index) => {
                      const flag = getStudentFlag(student)
                      return (
                      <tr key={student.st_id} className={flag ? flag.rowClass : ''}>
                        <th scope="row" className="text-center">{index + 1}</th>
                        <td className="text-center">{student.st_idnumber}</td>
                        <td>
                          {student.st_name}
                          {flag && <span className={`badge ${flag.badgeClass} ms-2`} style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>{flag.label}</span>}
                        </td>
                        {sessions.map(session => {
                          const status = attendanceMap.get(`${student.study_id}__${session.key}`) || ''
                          return (
                            <td key={session.key} className={`text-center ${getAttendanceClass(status)}`} style={{ padding: "2px", fontSize: "0.8rem", verticalAlign: "middle" }}>
                              {status}
                            </td>
                          )
                        })}
                      </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={3 + sessions.length} className="text-center">ไม่พบข้อมูล</td>
                    </tr>
                  )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  )
}